/**
 * Copyright 2023 Design Barn Inc.
 */

'use client';

import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useClient } from 'urql';

import { graphql } from '../../gql';
import type { ISessionResponse } from '../utils/api-requests';
import { deimosGetSession } from '../utils/api-requests';
import urlJoin from '../utils/url-join';
import { getFormattedSearchParamsString } from '../utils/url-params';

import { useIframeMessaging } from './iframe-messaging-context';

if (!process.env['NEXT_PUBLIC_DEIMOS_SESSION_URL']) {
  throw new Error('NEXT_PUBLIC_DEIMOS_SESSION_URL  environment variable is not set');
}

export interface AuthResult {
  accessToken: string;
  additionalAuthRequired: boolean;
  expiresAt: string;
  socialLoginAccountCreated: boolean | null;
  tokenType: 'Bearer' | 'Cookie';
}

export interface AuthSessionContextValue {
  handleAuthResult: ({
    mode,
    result,
    verifyEmail,
  }: {
    mode: string;
    result: AuthResult;
    verifyEmail?: boolean;
  }) => Promise<void>;
  isSessionChecked: boolean;
  loading: boolean;
  session: ISessionResponse | null;
}

const AuthSessionContext = createContext<AuthSessionContextValue>({
  loading: false,
  isSessionChecked: false,
  session: null,
  handleAuthResult: async () => Promise.resolve(),
});

const viewerQuery = graphql(`
  query viewer {
    viewer {
      email
      emailVerified
    }
  }
`);

export const AuthSessionProvider = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  const [session, setSession] = useState<ISessionResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isSessionChecked, setIsSessionChecked] = useState<boolean>(false);
  const searchParams = useSearchParams();
  const client = useClient();
  const { afterLogin, config, openAuthUrlInParent } = useIframeMessaging();
  const { metadata, origin, returnToPath, returnToUrl } = config;

  const router = useRouter();
  const pathname = usePathname();

  const { closeModal } = useIframeMessaging();

  const getBaseUrl = (url: string | undefined): string => {
    // eslint-disable-next-line node/no-unsupported-features/node-builtins
    const baseUrl = url ? new URL(url).origin : '';

    return baseUrl;
  };

  // this function checks if the user's email is verified
  // if the user's email is not verified, it will redirect to the verify-email page
  const checkIsEmailVerified = useCallback(
    async (token: string) => {
      const queryResult = await client.query(
        viewerQuery,
        {},
        {
          fetchOptions: {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        },
      );

      return queryResult.data?.viewer.emailVerified;
    },
    [client],
  );

  // this function is run on init and checks whether the user has an existing session
  // if the user has a session, it will set the session and close the modal except in the
  // case of logout, which needs the modal to stay open to complete the logout process
  const initAuthSession = async (): Promise<void> => {
    const deimosSession = await deimosGetSession();

    setIsSessionChecked(true);
    setLoading(false);

    if (deimosSession && deimosSession.token) {
      setSession(deimosSession);

      // we need to keep the modal open if it's a logout
      // so that the actions in the logout page can be completed
      // then the page will trigger closing the modal
      if (pathname.includes('/logout')) {
        return;
      }

      const verified = await checkIsEmailVerified(deimosSession.token);

      if (verified) {
        closeModal();
      }
    }
  };

  const baseUrl = getBaseUrl(origin);

  const returnTo = returnToUrl || urlJoin(baseUrl, returnToPath || '');

  // this is run once on the initial mount of the auth iframe app
  useEffect(() => {
    setLoading(true);

    initAuthSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // this function is run with the results of an authentication request
  const handleAuthResult = useCallback(
    async ({ result, verifyEmail }: { mode: string; result: AuthResult; verifyEmail?: boolean }): Promise<void> => {
      const params = getFormattedSearchParamsString(searchParams);

      if (result.tokenType.toLowerCase() !== 'cookie') {
        return;
      }

      // handle any prerequisite steps before getting the session
      if (result.additionalAuthRequired) {
        const mfaLinkHref = `/mfa${params}`;

        router.push(mfaLinkHref);

        return;
      }

      const sessionData = await deimosGetSession();

      if (!sessionData) {
        throw new Error('Internal server error: failed to validate session.');
      }

      if (!sessionData.active || !sessionData.expires_at || !sessionData.token) {
        throw new Error('Internal server error: invalid session response.');
      }

      setSession(sessionData);

      // run the email verification check if the feature is enabled
      if (verifyEmail) {
        if (!(await checkIsEmailVerified(sessionData.token))) {
          return;
        }
      }

      const iframeResult = await afterLogin('password', undefined, metadata);

      if (!iframeResult.success) {
        throw new Error('Internal error.');
      }

      if (returnToUrl) {
        openAuthUrlInParent(returnTo);
      }
    },
    [searchParams, afterLogin, metadata, returnToUrl, router, checkIsEmailVerified, openAuthUrlInParent, returnTo],
  );

  const contextValue = useMemo<AuthSessionContextValue>(
    () => ({
      loading,
      isSessionChecked,
      session,
      handleAuthResult,
    }),
    [loading, isSessionChecked, session, handleAuthResult],
  );

  return <AuthSessionContext.Provider value={contextValue}>{children}</AuthSessionContext.Provider>;
};

export const useAuthSession = (): AuthSessionContextValue => React.useContext(AuthSessionContext);
